import { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { Box, Button, ButtonBase, NameInput } from '@/shared/ui';

import { SAVED_FILTER_NAME_MAX_LENGTH } from '@/config/name';
import { createNameSchema } from '@/features/dashboard/lib/validateName';

const sectionSx = {
  px: 1.5,
  py: 1,
  flexShrink: 0,
  backgroundColor: 'gx.mainBackground',
  borderTop: '0.5px solid',
  borderTopColor: 'gx.border'
};

const nameInputSx = {
  '.MuiInputBase-root': {
    backgroundColor: 'gx.tileLight',
    '.MuiOutlinedInput-notchedOutline': {
      border: '0.5px solid',
      borderColor: 'gx.border'
    }
  }
};

const formSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%'
};

const buttonRowSx = {
  display: 'flex',
  gap: 1
};

const addButtonSx = {
  color: 'gx.highlight',
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 0.2
};

const saveButtonSx = {
  flex: 3,
  fontSize: 11,
  fontWeight: 700,
  '&.Mui-disabled': {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    opacity: 0.35
  }
};

const cancelButtonSx = {
  flex: 1,
  border: '0.5px solid',
  borderColor: 'gx.border',
  backgroundColor: 'gx.tileDark',
  color: 'gx.border',
  textAlign: 'center',
  fontFamily: 'Cairo',
  fontSize: 11
};

type NameField = {
  state: {
    meta: { errors: readonly unknown[] };
    value: string;
  };
  handleBlur: () => void;
  handleChange: (value: string) => void;
};

interface SaveCurrentFilterFormProps {
  existingNames: string[];
  canSaveCurrent: boolean;
  onSave: (name: string) => void;
}

const SaveCurrentFilterForm = ({
  existingNames,
  canSaveCurrent,
  onSave
}: SaveCurrentFilterFormProps) => {
  const [isAdding, setIsAdding] = useState(false);

  const nameSchema = useMemo(
    () =>
      z.object({
        name: createNameSchema('Filter name', SAVED_FILTER_NAME_MAX_LENGTH, existingNames)
      }),
    [existingNames]
  );

  const form = useForm({
    defaultValues: { name: '' },
    validators: { onChange: nameSchema },
    onSubmit: ({ value }) => {
      onSave(value.name.trim());
      form.reset();
      setIsAdding(false);
    }
  });

  if (!canSaveCurrent && !isAdding) return null;

  const cancel = () => {
    form.reset();
    setIsAdding(false);
  };

  return (
    <Box sx={sectionSx}>
      {isAdding ? (
        <form.Field name='name'>
          {field => {
            const typedField = field as unknown as NameField;
            const hasErrors = typedField.state.meta.errors.length > 0;
            return (
              <Box sx={formSx}>
                <NameInput
                  isEmpty={false}
                  maxLength={SAVED_FILTER_NAME_MAX_LENGTH}
                  placeholder='Name this filter...'
                  size={11}
                  sx={nameInputSx}
                  value={typedField.state.value}
                  onBlur={typedField.handleBlur}
                  onChange={typedField.handleChange}
                />
                <Box sx={buttonRowSx}>
                  <Button
                    disabled={hasErrors || !typedField.state.value.trim()}
                    size='small'
                    sx={saveButtonSx}
                    variant='contained'
                    onClick={() => form.handleSubmit()}
                  >
                    Save
                  </Button>
                  <Button
                    size='small'
                    sx={cancelButtonSx}
                    variant='outlined'
                    onClick={cancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            );
          }}
        </form.Field>
      ) : (
        <ButtonBase
          sx={addButtonSx}
          onClick={() => {
            setIsAdding(true);
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
          Save current filters
        </ButtonBase>
      )}
    </Box>
  );
};

export default SaveCurrentFilterForm;
